import React, {useCallback, useMemo, useState} from 'react';
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../../utils/localization-ag-grid";
import Button from "../../../components/button";
import RemoveButtonCellRenderer from "../../../components/cell-renderer/remove-button-cell-renderer";
import Modal from "../../../components/modal";
import InputForm from "../../../components/input-form";
import SelectComponent from "../../../components/select-component";
import InputCheckbox from "../../../components/input-checkbox";
import {accessList} from "./accessList";
import EquipmentButtons from "./equipment-buttons";
import {toast} from "react-toastify";
import {now} from "moment";

const SshTable = ({data, gridRef, wrapperClass, resume = false, tableClass, readOnlyTable, onClose, submit, onlyClose, autoSubmitOnModalSave = true}) => {
    const [open, setOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState("")
    const [editingRowIndex, setEditingRowIndex] = useState(null)
    const [modalData, setModalData] = useState([])
    const [newRow, setNewRow] = useState(false)
    const [modalApi, setModalApi] = useState(null)
    const [modalNode, setModalNode] = useState(null)

    const modalRender = (params, title, api, node) => {
        setModalNode(node)
        setModalApi(api)
        setModalData(params.node.data)
        setEditingRowIndex(params.node.id);
        setModalTitle(title)
        setOpen(true);
    }

    const [columnDefs] = useState( () => [
        {field: "name", headerName: "Nome", flex: 1, minWidth: 300},
        {field: "ip", headerName: "IP:Porta", flex: 1, minWidth: 300},
        {field: "is_main", headerName: "Principal", cellClass: "justify-center", sortable: false, width: 80},
        {field: "type", headerName: "Tipo de conexão", sortable: false, width: 250},
        {
            field: "",
            sortable: false,
            width: 100,
            editable: false,
            cellRenderer: EquipmentButtons,
            cellRendererParams: {
                modalRender: modalRender,
                title: "SSH"
            }
        }
    ]);

    const [readOnlyColumnDefs] = useState( () => [
        {field: "name", headerName: "Nome", flex: 1, minWidth: 300},
        {field: "ip", headerName: "IP:Porta", flex: 1, minWidth: 300},
        {field: "is_main", headerName: "Principal", cellClass: "justify-center", cellEditor: "agCheckboxCellEditor", sortable: false, width: 80},
        {field: "type", headerName: "Tipo de conexão", sortable: false, width: 250},
        ]);

    const defaultColDef = useMemo(() => ({resizable: false, editable: false}), []);

    const showColDef = useMemo(() => ({editable: false, suppressMenu: true, cellStyle: { cursor: 'default' }}), []);

    const addSsh = useCallback((modalData) => {
        const newSsh = {
            name: modalData.name,
            ip: modalData.ip,
            is_main: modalData.is_main,
            type: modalData.type,
            access_address: modalData.access_address,
            id: "uuid-" + now(),
        }

        gridRef.current?.api.applyTransaction({add: [newSsh]});
        setNewRow(false)
        if (autoSubmitOnModalSave) submit();
    }, [autoSubmitOnModalSave, gridRef, submit]);

    const handleInput = (field, value) => {
        setModalData(prev => ({...prev, [field]: value}))
    };
    const handleSelect = (id, value) => {
        setModalData(prev => ({...prev, [id]: value.label}))
    };

    const handleCheckBox = (e) => {
        const checked = e.target.checked;

        setModalData(prev => ({...prev, is_main: checked}))
    }

    const onCellClicked = (cell) => {
        if (cell.value) {
            const columnName = cell.colDef.headerName;
            if (columnName === "IP:Porta"){
                let textArea = document.createElement("textarea");
                textArea.value = cell.value;
                textArea.style.position = "fixed";
                textArea.style.left = "-999999px";
                textArea.style.top = "-999999px";
                document.body.appendChild(textArea);

                textArea.focus();
                textArea.select();
                return new Promise((res, rej) => {
                    document.execCommand('copy') ? res() : rej();
                    textArea.remove();
                    toast.success("IP:Porta copiado");
                });
            }
        }
    };

    const findConnectionType = (list, givenName) => {
        let connectionName

        connectionName = list?.find(opt => opt.label?.includes(givenName))

        if (connectionName === undefined || connectionName === '' || connectionName === null){
            connectionName = givenName ?? ""
        }

        return connectionName;
    }

    return (
        <div className={wrapperClass}>
            <div className={"ag-theme-quartz grid mt-4 " + tableClass}>
                <AgGridReact
                    ref={gridRef}
                    rowHeight={32}
                    rowData={data}
                    columnDefs={ readOnlyTable ? readOnlyColumnDefs : columnDefs}
                    defaultColDef={!resume ? defaultColDef : showColDef}
                    animateRows={false}
                    suppressRowClickSelection={true}
                    gridOptions={{localeText: LocalizationAgGrid}}
                    singleClickEdit={true}
                    stopEditingWhenCellsLoseFocus={true}
                    domLayout='autoHeight'
                    suppressCellFocus={resume}
                    context={{ readOnlyTable }}
                    onCellClicked={onCellClicked}
                />
            </div>

            {
                !resume &&
                <Button onClick={() => {
                    setModalTitle("Contato")
                    setOpen(true);
                    setNewRow(true)
                    setModalData([])
                }} target="primary" col="mt-3">
                    Adicionar SSH
                </Button>
            }

            <Modal isOpen={open} onClose={() => {onlyClose(setOpen, setNewRow)}} title={modalTitle} overflow={true}>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12 m-5">
                    <InputForm id="name" name="name" value={modalData.name} label="Nome" col="col-span-4"
                               onChange={({ target: { value } }) => handleInput("name", value)} />

                    <InputForm id="ip" name="ip" value={modalData.ip} label="IP:Porta" col="col-span-4"
                               onChange={({ target: { value } }) => handleInput("ip", value)}/>

                    <InputCheckbox id={"is_main"} name={"is_main"} label={"Principal"} col="col-span-12 lg:col-span-1"
                                   onChange={(e) => handleCheckBox(e)}
                                   checked={modalData.is_main}
                    />

                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            instanceId="type"
                            label="Tipo de conexão"
                            options={accessList}
                            value={findConnectionType(accessList, modalData.type)}
                            onChange={(value) => handleSelect("type", value)}
                            name="type"
                            formatCreateLabel={(inputValue) => `${inputValue}`}
                        />
                    </div>

                </div>
                <div className="flex justify-end gap-2 px-3 py-3 border-t border-gray-300 bg-[#F2F2F2] rounded-b-lg">
                    <div className="flex justify-end gap-3">
                        {
                            !newRow &&
                            <RemoveButtonCellRenderer node={modalNode} api={modalApi} modal={true} setOpen={setOpen}></RemoveButtonCellRenderer>
                        }
                        {
                            newRow ?
                                <Button target="success" onClick={() => {
                                    if(newRow) addSsh(modalData)
                                    setOpen(false);
                                }}>
                                    Salvar
                                </Button>
                                :
                                <Button target="success" onClick={() => onClose(setOpen, setNewRow, gridRef, editingRowIndex, modalData, autoSubmitOnModalSave)}>
                                    Salvar
                                </Button>
                        }
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default SshTable;
